import React from 'react';
import { Moon, Sun, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PolicyGenerator } from '@/components/PolicyGenerator';
import { openaiService } from '@/services/openaiService';

function App() {
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  const hasApiKey = openaiService.hasApiKey();

  // Handle dark mode toggle
  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background gradient-subtle">
        {/* Background decoration */}
        <div className="fixed inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        
        {/* Header */}
        <header className="sticky top-0 z-50 glass border-b">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl gradient-primary shadow-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-display text-xl font-semibold tracking-tight">
                  AI Policy Producer
                </h1>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  Generate AI use policies for academic tasks
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDarkMode(!darkMode)}
                    className="rounded-full"
                  >
                    {darkMode ? (
                      <Sun className="h-5 w-5" />
                    ) : (
                      <Moon className="h-5 w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="container mx-auto px-4 py-8">
          {!hasApiKey ? (
            <div className="max-w-2xl mx-auto">
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center space-y-4">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                <h2 className="text-xl font-semibold">API Key Required</h2>
                <p className="text-muted-foreground">
                  Please set the <code className="bg-muted px-2 py-1 rounded text-sm">VITE_OPENAI_API_KEY</code> environment variable in your <code className="bg-muted px-2 py-1 rounded text-sm">.env</code> file to use this application.
                </p>
                <div className="bg-muted rounded-lg p-4 text-left">
                  <p className="text-sm font-mono text-muted-foreground">
                    # .env file<br />
                    VITE_OPENAI_API_KEY=sk-your-api-key-here
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <PolicyGenerator />
          )}
        </main>

        {/* Footer */}
        <footer className="border-t mt-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
              <p>
                Built for educators to create clear AI use policies
              </p>
              <p className="flex items-center gap-1">
                This application is an extension of
                <a
                  href="https://www.smartie.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  SMARTIE 2.0
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}

export default App;
